import {
  Form,
  Input,
  ModalProps,
  FormProps,
  Modal,
  InputNumber,
  Row,
  Col,
  Select,
  useSelect,
  Divider,
  Spin,
  Button,
  Typography,
  notification,
  Icons,
  message,
} from '@pankod/refine-antd'
import type { NotificationPlacement } from 'antd/es/notification'
import { useGetIdentity, useTable } from '@pankod/refine-core'
import { useUserContext } from 'context/user'
import {
  IBloomLevel,
  ICompetency,
  IDifficultyLevel,
  IQuestion,
  IQuestionType,
  IReportTheme,
  IStrand,
  ISubject,
  ISubTheme,
  ITheme,
  ITNTaxonomy,
  ITopic,
} from 'interfaces'
import React from 'react'
import { useState } from 'react'
import { questionEditTemplateFormat, questionTemplateFormat } from '../../helpers/import_template_format/importTemplate'
import { CSVLink } from 'react-csv'

type UploadQuestionProps = {
  modalProps: ModalProps
  close: any // formProps: FormProps
  reloadUploadedData: any
}
const { ExportOutlined } = Icons
export const UploadQuestions: React.FC<UploadQuestionProps> = ({ modalProps, close, reloadUploadedData }) => {
  const { data: user } = useGetIdentity()
  const { userRole, qapproverList, qcreatorList, qreviewerList, vcreatorList } = useUserContext()
  const [form] = Form.useForm<IQuestion>()
  const [subjectFilter, setSubjectFilter] = useState<any[]>([])
  const [taxonomyfilter, setTaxonomyFilter] = useState([])
  const [strandfilter, setStrandFilter] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [showMessage, setShowMessage] = useState(false)
  const [count, setCount] = useState(0)
  const [response, setResponse] = useState('')
  const [fileName, setFile] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // const { userRole } = useUserContext()
  // const [file, setFile] = useState<FileDetails>()
  const fileReader = new FileReader()
  const [array, setArray] = useState<object[]>([])

  const { selectProps: subjectSelectProps } = useSelect<ISubject>({
    resource: 'subjects',
    fetchSize: 100,
    optionLabel: 'name',
    optionValue: 'id',
    metaData: {
      fields: ['id', 'name'],
    },
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: value => [
      {
        field: 'name',
        operator: 'contains',
        value: `${value.toLowerCase()}%`,
      },
    ],
  })
  const { selectProps: questionTypeSelectProps } = useSelect<IQuestionType>({
    resource: 'question_types',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    metaData: {
      fields: ['id', 'name'],
    },
    sort: [{ field: 'name', order: 'asc' }],
  })
  const { selectProps: reportThemeSelectProps } = useSelect<IReportTheme>({
    resource: 'report_themes',
    fetchSize: 100,
    optionLabel: 'name',
    optionValue: 'id',
    metaData: {
      fields: ['id', 'name'],
    },
    filters: subjectFilter,
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: value => [
      {
        field: 'name',
        operator: 'contains',
        value: `${value.toLowerCase()}%`,
      },
    ],
  })
  const { filters, setFilters, tableQueryResult, sorter } = useTable<ITopic>({
    resource: 'topics',
    metaData: { fields: ['id', 'name', 'sub_theme_id'] },
    initialPageSize: 500,
  })
  const {
    filters: themeFilters,
    setFilters: setThemeFilters,
    tableQueryResult: themeResult,
  } = useTable<ITheme>({
    resource: 'themes',
    metaData: { fields: ['id', 'name', 'subject_id'] },
  })

  const {
    filters: subThemeFilters,
    setFilters: setSubThemeFilters,
    tableQueryResult: subThemeResult,
  } = useTable<ISubTheme>({
    resource: 'sub_themes',
    metaData: { fields: ['id', 'name', 'theme_id'] },
  })
  const { selectProps: competencySelectProps } = useSelect<ICompetency>({
    resource: 'competencies',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    metaData: {
      fields: ['id', 'name'],
    },
    sort: [{ field: 'name', order: 'asc' }],
  })
  const { selectProps: tntaxonomySelectProps } = useSelect<ITNTaxonomy>({
    resource: 'tn_taxonomys',
    fetchSize: 100,
    optionLabel: 'name',
    optionValue: 'id',
    metaData: {
      fields: ['id', 'name', 'competency_id'],
    },
    sort: [{ field: 'name', order: 'asc' }],
    filters: taxonomyfilter,
    onSearch: value => [
      {
        field: 'name',
        operator: 'contains',
        value: `${value.toLowerCase()}%`,
      },
    ],
  })
  const { selectProps: bloomlevelSelectProps } = useSelect<IBloomLevel>({
    resource: 'blooms_levels',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    metaData: {
      fields: ['id', 'name'],
    },
    sort: [{ field: 'order', order: 'asc' }],
  })
  const { selectProps: difficultylevelSelectProps } = useSelect<IDifficultyLevel>({
    resource: 'difficulty_levels',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    metaData: {
      fields: ['id', 'name'],
    },
    sort: [{ field: 'order', order: 'asc' }],
  })
  const { selectProps: strandSelectProps } = useSelect<IStrand>({
    resource: 'strands',
    fetchSize: 100,
    optionLabel: 'name',
    optionValue: 'id',
    metaData: {
      fields: ['id', 'name', 'teacher_level_id', 'taxonomy_id', 'competency_id'],
    },
    sort: [{ field: 'name', order: 'asc' }],
    filters: strandfilter,
    onSearch: value => [
      {
        field: 'name',
        operator: 'contains',
        value: `${value.toLowerCase()}%`,
      },
    ],
  })
  const handleSubjectChange = (value: { value: any }) => {
    setSubjectFilter([])
    form?.setFieldsValue({
      sub_theme_id: '',
      topic_id: '',
      theme_id: '',
      report_theme_id: '',
    })
    // setReportThemeData([])

    let data: any = [
      {
        field: 'subject_id',
        operator: 'eq',
        value: value,
      },
    ]
    setSubjectFilter(data)
  }
  const handleReportThemeChange = (value: { value: any }) => {
    themeFilters.pop()
    form?.setFieldsValue({
      sub_theme_id: '',
      topic_id: '',
      theme_id: '',
    })
    setThemeFilters([
      {
        field: 'report_theme_id',
        operator: 'eq',
        value: value,
      },
    ])
  }
  const handleThemeChange = (value: { value: any }) => {
    subThemeFilters.pop()
    form?.setFieldsValue({
      sub_theme_id: '',
      topic_id: '',
    })
    setSubThemeFilters([
      {
        field: 'theme_id',
        operator: 'eq',
        value: value,
      },
    ])
  }
  const handleSubThemeChange = (value: { value: any }) => {
    form?.setFieldsValue({
      topic_id: '',
    })
    setFilters([
      {
        field: 'sub_theme_id',
        operator: 'eq',
        value: value,
      },
    ])
  }
  const handleCompetencyChange = (value: { value: any }) => {
    form?.setFieldsValue({
      teacher_level_id: '',
      strand_id: '',
      tn_taxonomy_id: '',
    })
    let data: any = [
      {
        field: 'competency_id',
        operator: 'eq',
        value: value,
      },
    ]
    setTaxonomyFilter(data)
    setStrandFilter(data)
  }
  const handleTaxonomyChange = (value: { value: any }) => {
    form?.setFieldsValue({
      teacher_level_id: '',
      strand_id: '',
    })
    let arr: any[] = [...strandfilter]
    let data: any = {
      field: 'taxonomy_id',
      operator: 'eq',
      value: value,
    }
    arr.push(data)

    setStrandFilter(arr)
  }
  const handleOnChange = (e: any) => {
    console.log('first', e.target.files[0])
    //  setBackground('opacity-blur')
    setLoading(true)
    // setLoading(true)
    // setShowUpload(false)
    setFile(e.target.files[0]['name'])
    // increase(20)
    handleOnSubmit(e.target.files[0])
  }
  const handleOnSubmit = (fs: any) => {
    if (fs) {
      fileReader.onload = function (event: any) {
        const csvOutput = event.target.result
        setArray(csvOutput)
        setLoading(false)
        // uploadQuestions(csvOutput)
        // csvFileToArray(csvOutput)
        //  increase(50)
      }

      fileReader.readAsText(fs)
    }
  }

  const openNotification = (placement: NotificationPlacement, message: string) => {
    notification.success({
      message: `Notification`,
      description: message,
      placement,
    })
    reloadUploadedData()
  }
  const onFinish = (values: any) => {
    setUploading(true)
    let details: object[] = []
    setErrorMessage('')
    details.push({ csv: array, created_by: user?.id, language: values.language || 'english' })
    // console.log(process.env.REACT_APP_BACKEND_URL,": backend url")
    const url = userRole !== 'subjectincharge' ? `upload-questions` : `question/bulk-edit`
    fetch(`${process.env.REACT_APP_BACKEND_URL}/${url}`, {
    // fetch(`http://localhost:3001/${url}`, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      body: JSON.stringify(details),
      headers: { 'Content-Type': 'application/json', charset: 'utf-8' },
    })
      .then(response => response.json())
      .then((data: any) => {
        if (data?.statusMessage === 'Success') {
          // openNotification('bottomRight', 'Upload Success')
          form?.resetFields()
          setCount(data?.count)
          // close()
          setLoading(false)
          setUploading(false)
          setFile('')
          setResponse('success')
          message.success(data?.count ? `Uploaded ${data?.count} questions succesfully` : data?.message ? data.message : `Task finished!`)
          // setShowMessage(true)
          reloadUploadedData()
          close()
        } else {
          message.error(data?.message ? data?.message : 'Something went wrong,  Faild to upload questions')
          console.log(data, 'data is this')
          // openNotification('bottomRight', data?.statusMessage)
          form?.resetFields()
          // close()
          setLoading(false)
          setUploading(false)
          // reloadUploadedData
          setFile('')
          setResponse('error')
          setErrorMessage(data.errorExplanation || '')
          setShowMessage(true)
        }
      })
  }
  const onInputClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const element = event.target as HTMLInputElement
    element.value = ''
  }
  const csvData = [questionTemplateFormat.map(item => item.toString())]
  const quesEditCsvData = [questionEditTemplateFormat.map(item => item.toString())]
  return (
    <Modal
      {...modalProps}
      title={userRole === 'subjectincharge' ? 'Question Bulk Edit' : 'Upload Questions'}
      width={700}
      footer={[
        <Spin spinning={uploading} tip="Wait...Uploading..." />,
        <Button
          key="back"
          onClick={() => {
            form?.resetFields()
            setLoading(false)
            setUploading(false)
            setShowMessage(false)
            setResponse('')
            setCount(0)
            close()
            setErrorMessage('')
          }}
        >
          Cancel
        </Button>,

        // <Button key="submit" disabled={uploading}>
        //   Import Upload Format
        // </Button>,

        <Button key="submit" type="primary" loading={uploading} onClick={form.submit} disabled={uploading}>
          {userRole !== 'subjectincharge' ? `Upload` : `Edit Question`}
        </Button>,
      ]}
    >
      <Form form={form} size="large" layout="vertical" onFinish={onFinish} initialValues={{ language: 'english' }}>
        {userRole !== 'subjectincharge' && (
          <Row gutter={[16, 0]}>
            <Col xs={24} lg={24}>
              <Form.Item
                label="Language"
                name="language"
                rules={[{ required: true, message: 'Please select a language' }]}
                extra="Used for all uploaded questions when the CSV Language column is empty. For bilingual questions choose English-Hindi (or write English-Hindi in the CSV Language column)."
              >
                <Select
                  options={[
                    { value: 'english', label: 'English' },
                    { value: 'hindi', label: 'Hindi' },
                    { value: 'sanskrit', label: 'Sanskrit' },
                    { value: 'english-hindi', label: 'English-Hindi' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={24}>
            <div className="upload-btn-wrapper1">
              <button className="btnshow">Upload a file</button>
              <input type="file" name="myfile" id={'csvFileInput'} accept={'.csv'} onChange={handleOnChange} onClick={onInputClick} />
            </div>
            <Typography.Text type="success">{fileName}</Typography.Text>
          </Col>
        </Row>
        <Spin spinning={loading} tip="Wait...Uploading..." />
        <Form.Item name="created_by" initialValue={user?.id}>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="user_type" initialValue={userRole}>
          <Input type="hidden" />
        </Form.Item>

        <CSVLink
          key="export"
          data={userRole !== 'subjectincharge' ? csvData : quesEditCsvData}
          filename={userRole !== 'subjectincharge' ?`question_import_format${Math.round(new Date().getTime() / 1000)}`:`question_bulk_edit_format${Math.round(new Date().getTime() / 1000)}`}
        >
          <ExportOutlined /> {userRole !== 'subjectincharge' ? `Download AMS Question Import Format` : `Download AMS Question Bulk Edit Format`}
        </CSVLink>
        {errorMessage != '' && <Typography.Text type="danger">{errorMessage}</Typography.Text>}
        {/* <Form.Item name="created_by" initialValue={}>
          <Input type="hidden" />
        </Form.Item> */}
      </Form>

      {/* <table>
        <thead>
          <tr key={"header"}>
            {headerKeys.map((key) => (
              <th>{key}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {array.map((item) => (
            <tr key={item.id}>
              {Object.values(item).map((val) => (
                <td>{val}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table> */}
    </Modal>
  )
}